 import { motion } from 'framer-motion';
import { FormStepProps, FinalQuestionsData } from '../../../types/applicationTypes';

export default function Step4FinalQuestions({ data, errors, updateData }: FormStepProps) {
  // Ensure finalQuestions exists (handles edge cases with old data)
  const finalQuestions = data.finalQuestions || { howDidYouHear: '', acknowledgment: false };
  const finalErrors = errors?.finalQuestions ?? {};

  const inputClass = (hasError: boolean) =>
    `w-full px-4 py-3 border rounded-lg transition-all duration-200 bg-white ${
      hasError
        ? 'border-red-500 focus:ring-2 focus:ring-red-300 focus:border-red-500'
        : 'border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent'
    }`;

  const handleInputChange = (field: keyof FinalQuestionsData, value: string | boolean) => {
    updateData({
      finalQuestions: {
        ...finalQuestions,
        [field]: value
      }
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-dblue mb-2">Final Questions</h3>
        <p className="text-gray-600">Just a couple more questions to complete your application</p>
      </div>

      <div className="space-y-6">
        {/* How did you hear about us */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="space-y-3"
        >
          <label className="block text-sm font-semibold text-gray-700">
            How did you hear about Harbourside Children's Centre? *
          </label>
          <input
            type="text"
            value={finalQuestions.howDidYouHear}
            onChange={(e) => handleInputChange('howDidYouHear', e.target.value)}
            placeholder="Please tell us how you heard about us..."
            className={inputClass(Boolean(finalErrors.howDidYouHear))}
            required
            aria-invalid={Boolean(finalErrors.howDidYouHear)}
          />
          {finalErrors.howDidYouHear && (
            <p className="text-sm text-red-600">{finalErrors.howDidYouHear}</p>
          )}
        </motion.div>

        {/* Acknowledgment */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-dblue">Important Information</h4>
            
            <div className="bg-white rounded-lg p-4 border border-blue-100">
              <p className="text-gray-700 leading-relaxed">
                I acknowledge that once a space has been secured for my child, I am required to pay a 
                <strong> non-refundable application fee of $150.00</strong> and a 
                <strong> non-refundable 'Space Deposit'</strong> equivalent to half a month's fees.
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                <em>*The 'Space Deposit' will be applied toward your child's first month's fees.</em>
              </p>
            </div>

            <div className="flex items-start space-x-4">
              <input
                type="checkbox"
                id="acknowledgment"
                checked={finalQuestions.acknowledgment}
                onChange={(e) => handleInputChange('acknowledgment', e.target.checked)}
                className="mt-0.5 h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                required
                aria-invalid={Boolean(finalErrors.acknowledgment)}
              />
              <label htmlFor="acknowledgment" className="text-sm text-gray-700 cursor-pointer leading-relaxed">
                <strong>  I acknowledge and agree to the above terms regarding the application fee and space deposit. *</strong>
              </label>
            </div>
            {finalErrors.acknowledgment && (
              <p className="text-sm text-red-600">{finalErrors.acknowledgment}</p>
            )}
          </div>
        </motion.div>

        {/* Additional Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="bg-green-50 border border-green-200 rounded-lg p-6"
        >
          <div className="flex items-start space-x-3">
            <svg className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-green-800 mb-2">Almost Done!</h4>
              <p className="text-green-700 text-sm leading-relaxed">
                You're just one step away from completing your application. After submitting, 
                our team will review your application and contact you within 2-3 business days 
                to discuss next steps and schedule a tour.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}
